import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, MapPin, Car, Check, ChevronLeft, ChevronRight, Clock, Calendar, Star, User } from "lucide-react";
import api from "../lib/api";
import { formatMoney } from "../lib/money";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Scrollable rail of real luxury vehicles registered by nearby suppliers.
 * All luxury rentals are chauffeur-driven (self-drive was removed).
 *
 * Customer picks a specific car, then picks their billing model:
 *   - per_trip  (default, distance-based)
 *   - hourly    (× number of hours, using vehicle.luxury_hourly_rate)
 *   - daily     (× number of days, using vehicle.luxury_daily_rate)
 */
export default function LuxuryVehiclePicker({
  pickupLat,
  pickupLng,
  currency,
  selectedVehicleId,
  rentalType,
  rentalDuration,
  onSelect,
  onRentalTypeChange,
  onRentalDurationChange,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState({}); // vehicle_id → photo index

  useEffect(() => {
    if (pickupLat == null || pickupLng == null) return;
    setLoading(true);
    api
      .get("/luxury/nearby", { params: { lat: pickupLat, lng: pickupLng, radius_km: 40 } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [pickupLat, pickupLng]);

  const selectedVehicle = useMemo(
    () => items.find((v) => v.vehicle_id === selectedVehicleId) || null,
    [items, selectedVehicleId]
  );

  const canHourly = !!selectedVehicle?.hourly_rate;
  const canDaily = !!selectedVehicle?.daily_rate;

  return (
    <div className="mb-3" data-testid="luxury-picker">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
        {t("request.luxuryRailTitle", "Luxury cars near you")}
      </div>

      {pickupLat == null ? (
        <div className="text-xs text-white/40 bg-white/[0.03] rounded-xl p-3 border border-white/10">
          {t("request.setPickupFirst", "Set your pickup to see cars available in your area")}
        </div>
      ) : loading ? (
        <div className="text-xs text-white/40 bg-white/[0.03] rounded-xl p-3 border border-white/10 inline-flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> {t("request.looking", "Looking…")}
        </div>
      ) : items.length === 0 ? (
        <div className="text-xs text-white/60 bg-white/[0.03] rounded-xl p-3 border border-white/10">
          {t("request.noLuxuryNearby", "No luxury cars available in your area yet — try widening the pickup area or check back soon.")}
        </div>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 snap-x snap-mandatory" data-testid="luxury-rail">
            {items.map((v, i) => {
              const isSelected = v.vehicle_id
                ? v.vehicle_id === selectedVehicleId
                : selectedVehicleId === `supplier:${v.supplier_id}`;
              const photos = (v.photos && v.photos.length ? v.photos : (v.photo ? [v.photo] : []));
              const idx = galleryIdx[v.vehicle_id] || 0;
              const current = photos[idx];
              const key = v.vehicle_id || `sup-${v.supplier_id}-${i}`;
              return (
                <div
                  key={key}
                  className={`snap-start shrink-0 w-56 rounded-2xl border-2 text-start overflow-hidden transition-all ${
                    isSelected
                      ? "border-amber-gold shadow-gold bg-white/[0.06]"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                  data-testid={`luxury-car-${v.vehicle_id || v.supplier_id}`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(v)}
                    className="relative h-28 w-full bg-black/40 block"
                  >
                    {current ? (
                      <img src={current} alt={v.model || v.label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30">
                        <Car size={36} />
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-amber-gold text-obsidian flex items-center justify-center">
                        <Check size={14} strokeWidth={3} />
                      </div>
                    )}
                    {photos.length > 1 && (
                      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
                        {photos.map((_, pi) => (
                          <span
                            key={pi}
                            className={`w-1.5 h-1.5 rounded-full ${pi === idx ? "bg-white" : "bg-white/40"}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                  {photos.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGalleryIdx((g) => ({ ...g, [v.vehicle_id]: (idx - 1 + photos.length) % photos.length }));
                        }}
                        className="absolute top-14 -mt-3 left-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                        data-testid={`luxury-gallery-prev-${v.vehicle_id}`}
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGalleryIdx((g) => ({ ...g, [v.vehicle_id]: (idx + 1) % photos.length }));
                        }}
                        className="absolute top-14 -mt-3 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                        data-testid={`luxury-gallery-next-${v.vehicle_id}`}
                      >
                        <ChevronRight size={14} />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => onSelect(v)}
                    className="p-2.5 w-full text-start"
                  >
                    <div className="text-sm font-black text-white truncate">
                      {v.model || v.label || t("request.luxuryVehicle", "Luxury vehicle")}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">
                      {v.plate || ""}{v.plate && v.color ? " · " : ""}{v.color || ""}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-white/60">
                      <MapPin size={10} /> {v.distance_km?.toFixed?.(1) ?? v.distance_km} km
                    </div>
                    <div className="mt-1 flex gap-1 flex-wrap">
                      <span className="text-[9px] font-black uppercase bg-amber-gold/20 text-amber-gold rounded-full px-1.5 py-0.5 tracking-widest">
                        {t("luxury.with_driver", "With driver")}
                      </span>
                      {v.hourly_rate ? (
                        <span className="text-[9px] font-black uppercase bg-white/10 text-white/80 rounded-full px-1.5 py-0.5 tracking-widest">
                          {formatMoney(v.hourly_rate, currency)}/{t("luxury.hr", "hr")}
                        </span>
                      ) : null}
                      {v.daily_rate ? (
                        <span className="text-[9px] font-black uppercase bg-white/10 text-white/80 rounded-full px-1.5 py-0.5 tracking-widest">
                          {formatMoney(v.daily_rate, currency)}/{t("luxury.day", "day")}
                        </span>
                      ) : null}
                    </div>
                    <ChauffeurBio v={v} />
                  </button>
                </div>
              );
            })}
          </div>

          {selectedVehicle && (canHourly || canDaily) && (
            <div className="mt-3" data-testid="luxury-rental-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
                {t("request.luxuryBilling", "Billing")}
              </div>
              <div className="flex gap-2 mb-2 flex-wrap">
                <RentalModeBtn active={rentalType === "per_trip" || !rentalType} onClick={() => onRentalTypeChange("per_trip")} testid="luxury-rental-per_trip">
                  {t("luxury.per_trip", "Per trip")}
                </RentalModeBtn>
                {canHourly && (
                  <RentalModeBtn active={rentalType === "hourly"} onClick={() => onRentalTypeChange("hourly")} testid="luxury-rental-hourly">
                    <Clock size={12} className="mr-1 inline" />
                    {t("luxury.hourly", "Hourly")} · {formatMoney(selectedVehicle.hourly_rate, currency)}
                  </RentalModeBtn>
                )}
                {canDaily && (
                  <RentalModeBtn active={rentalType === "daily"} onClick={() => onRentalTypeChange("daily")} testid="luxury-rental-daily">
                    <Calendar size={12} className="mr-1 inline" />
                    {t("luxury.daily", "Daily")} · {formatMoney(selectedVehicle.daily_rate, currency)}
                  </RentalModeBtn>
                )}
              </div>
              {(rentalType === "hourly" || rentalType === "daily") && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-1">
                    {rentalType === "hourly" ? t("luxury.hours", "Hours") : t("luxury.days", "Days")}
                  </div>
                  <input
                    type="number"
                    min="1"
                    max={rentalType === "hourly" ? 24 : 30}
                    value={rentalDuration || ""}
                    onChange={(e) => onRentalDurationChange(Number(e.target.value) || 0)}
                    placeholder={rentalType === "hourly" ? "e.g. 4" : "e.g. 2"}
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-amber-gold"
                    data-testid="luxury-rental-duration"
                  />
                  {rentalType === "daily" && rentalDuration >= 3 && (
                    <div
                      className="mt-2 text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1.5 inline-block"
                      data-testid="luxury-ladder-discount"
                    >
                      {ladderPreview(rentalDuration, t)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RentalModeBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3.5 py-2 text-xs font-semibold border transition-all ${
        active
          ? "bg-amber-gold text-obsidian border-amber-gold shadow-gold"
          : "bg-white/[0.04] text-white/80 border-white/10 hover:border-white/25"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function ChauffeurBio({ v }) {
  const { t } = useTranslation();
  const name = v.driver_name || v.supplier_name;
  const photo = v.driver_photo && v.driver_photo.startsWith("/api/")
    ? `${REACT_APP_BACKEND_URL}${v.driver_photo}`
    : v.driver_photo;
  const rating = v.driver_rating || 0;
  const trips = v.driver_trips || 0;
  if (!name) return null;
  return (
    <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2" data-testid={`luxury-chauffeur-${v.vehicle_id}`}>
      {photo ? (
        <img
          src={photo}
          alt={name}
          className="w-7 h-7 rounded-full object-cover border border-amber-gold/40 shrink-0"
          data-testid={`luxury-driver-photo-${v.vehicle_id}`}
        />
      ) : (
        <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0">
          <User size={12} className="text-white/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-black text-white truncate">{name}</div>
        <div className="text-[9px] text-white/60 flex items-center gap-1">
          {rating > 0 ? (
            <span className="inline-flex items-center gap-0.5 text-amber-gold font-black">
              <Star size={9} fill="currentColor" strokeWidth={0} /> {rating.toFixed(1)}
            </span>
          ) : (
            <span className="text-white/40">{t("luxury.newDriver", "New driver")}</span>
          )}
          {trips > 0 && <span>· {trips} {t("luxury.trips", "trips")}</span>}
        </div>
      </div>
    </div>
  );
}

// Multi-day ladder: 3-6d → 5% off, 7-14d → 10%, 15+d → 15%
function ladderPreview(days, t) {
  let pct = 0;
  if (days >= 15) pct = 15;
  else if (days >= 7) pct = 10;
  else if (days >= 3) pct = 5;
  if (pct === 0) return "";
  return `${t("luxury.ladderDiscount", "Long-rental discount")} · −${pct}%`;
}
